import {
  Avatar,
  Box,
  IconButton,
  SxProps,
  Theme,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Dispatch,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
  SetStateAction
} from "react";

type OptionCardProps = {
  avatarContent: string | ReactNode;
  title: string;
  passedProps?: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
  subtitle?: string | ReactNode;
  clearable?: boolean;
  deleteable?: boolean;
  selected?: boolean;
  setOption?: Dispatch<SetStateAction<any>>;
  clearFunction?: () => void;
  deleteFunction?: () => void;
  autoCompleteOnClick?: MouseEventHandler<HTMLLIElement> | undefined;
  onClick?: () => void;
  disabled?: boolean;
  hasShadow?: boolean;
  isMenuItem?: boolean;
  filledBackground?: boolean;
  bordered?: boolean;
  clickable?: boolean;
  rightContent?: JSX.Element;
  avatarImage?: string;
  boldTitle?: boolean;
};

export const OptionCard = ({
  avatarContent,
  passedProps,
  title,
  subtitle,
  clearable,
  deleteable,
  selected,
  setOption,
  clearFunction,
  deleteFunction,
  onClick,
  autoCompleteOnClick,
  disabled,
  hasShadow,
  isMenuItem,
  filledBackground,
  bordered,
  clickable,
  rightContent,
  avatarImage,
  boldTitle
}: OptionCardProps) => {
  const styling: SxProps<Theme> = {
    display: "flex",
    gap: 2,
    alignItems: "center",
    boxShadow: hasShadow ? 2 : 0
  };

  // Remove React's virtual key so it is not spread to the DOM element
  const { key: _omitKey, ...liProps } = passedProps ?? {};

  if (bordered) {
    styling.border = "3px solid";
    styling.borderColor = "#4C60851A";
  }

  if (clickable || clearable) {
    styling.cursor = "pointer";
  }

  if (!isMenuItem) {
    styling.borderRadius = "10px";
    styling.padding = 1;
    if (selected) {
      styling.border = "3px solid";
      styling.borderColor = disabled
        ? "gray"
        : clearable
        ? "primary.main"
        : "secondary.main";
    }
    if (filledBackground) {
      styling.backgroundColor = "light.main";
    }
  }

  return (
    <Box
      component="li"
      {...liProps}
      sx={styling}
      onClick={(event) => {
        if (!disabled) {
          if (autoCompleteOnClick) {
            autoCompleteOnClick(event);
          }
          if (onClick) {
            onClick();
          }
          if (clearable) {
            if (clearFunction) {
              clearFunction();
              return;
            }
            if (setOption) {
              setOption(undefined);
            }
          }
        }
      }}
    >
      {avatarImage && (
        <Avatar
          src={avatarImage}
          alt="Avatar"
          variant="square"
          sx={{
            width: 40,
            height: 40,
            bgcolor: "common.white",
            "& .MuiAvatar-img": {
              objectFit: "contain"
            }
          }}
        />
      )}
      {!avatarImage && <Avatar>{avatarContent}</Avatar>}
      <Box sx={{ opacity: disabled ? "50%" : "100%" }}>
        <Typography sx={{ fontWeight: boldTitle ? "bold" : "normal" }}>
          {title}
        </Typography>
        {subtitle && <Typography color={"#00000099"}>{subtitle}</Typography>}
      </Box>
      <Box display="flex" flex={1} />
      {rightContent && (
        <Box display="flex" alignItems="center">
          {rightContent}
        </Box>
      )}
      <Box display="flex">
        {deleteable && (
          <IconButton
            aria-label="delete"
            onClick={(event) => {
              if (deleteFunction) {
                event.stopPropagation();
                deleteFunction();
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
